import { useEffect, useState } from "react";
import { Loader2, RotateCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { parentApi } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";

// Simple 1-5 selector rendered as 5 round buttons.
function RatingPicker({ value, onChange, ariaLabel }) {
  return (
    <div className="flex gap-2" role="radiogroup" aria-label={ariaLabel}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          onClick={() => onChange(n)}
          className={
            "w-9 h-9 rounded-full text-sm font-medium border transition " +
            (value === n
              ? "bg-burns-red border-burns-red text-white"
              : "bg-zinc-950 border-white/20 text-zinc-400 hover:border-burns-gold")
          }
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export default function SurveyForm({ camps = [] }) {
  const { t } = useLanguage();
  const [campId, setCampId] = useState("");
  const [overall, setOverall] = useState(0);
  const [coach, setCoach] = useState(0);
  const [comm, setComm] = useState(0);
  const [recommend, setRecommend] = useState(0);
  const [favorite, setFavorite] = useState("");
  const [improved, setImproved] = useState("");
  const [suggestions, setSuggestions] = useState("");
  const [other, setOther] = useState("");
  const [sending, setSending] = useState(false);
  const [allSurveys, setAllSurveys] = useState([]);

  useEffect(() => {
    parentApi.get("/parent/surveys").then((r) => setAllSurveys(r.data || [])).catch(() => {});
  }, []);

  // Surveys are write-once per camp. If one exists, the form is replaced with
  // a read-only summary further below.
  const existingForCamp = campId ? allSurveys.find((s) => s.camp_id === campId) : null;
  useEffect(() => {
    // Reset the editable form whenever the picked camp changes.
    setOverall(0); setCoach(0); setComm(0); setRecommend(0);
    setFavorite(""); setImproved(""); setSuggestions(""); setOther("");
  }, [campId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!campId) {
      toast.error(t("parent.pickCampFirst"));
      return;
    }
    if (!overall || !coach || !comm || !recommend) {
      toast.error(t("parent.surveyAllRatings"));
      return;
    }
    setSending(true);
    try {
      const camp = camps.find((c) => c.id === campId);
      await parentApi.post("/parent/survey", {
        camp_id: campId,
        camp_name: camp?.name || "",
        overall_rating: overall,
        coach_rating: coach,
        communication_rating: comm,
        would_recommend: recommend,
        favorite_part: favorite,
        most_improved_skill: improved,
        suggestions,
        other_comments: other,
      });
      toast.success(t("parent.surveyThanks"));
      // Re-fetch so the form switches into read-only "you've already submitted" mode
      try {
        const r = await parentApi.get("/parent/surveys");
        setAllSurveys(r.data || []);
      } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSending(false);
    }
  };

  const Question = ({ label, value, onChange }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-b border-white/5 last:border-0">
      <Label className="text-zinc-300 text-sm">{label}</Label>
      <RatingPicker value={value} onChange={onChange} ariaLabel={label} />
    </div>
  );

  // Pending nudge — count camps without a survey on file
  const surveyedIds = new Set(allSurveys.map((s) => s.camp_id));
  const pendingCount = camps.filter((c) => !surveyedIds.has(c.id)).length;

  return (
    <form onSubmit={submit} className="space-y-4">
      {pendingCount > 0 && !campId && (
        <div className="bg-burns-gold/10 border border-burns-gold/40 rounded-md p-3 text-burns-gold text-sm flex items-start gap-2">
          <span className="font-bold">★</span>
          <span>{t("parent.surveyPendingNudge").replace("{n}", pendingCount).replace("{plural}", pendingCount === 1 ? "" : "s")}</span>
        </div>
      )}
      <div>
        <Label className="text-zinc-300 text-sm mb-1 block">{t("parent.surveyCampLabel")}</Label>
        <Select value={campId} onValueChange={setCampId}>
          <SelectTrigger className="bg-zinc-950 border-white/20 text-white" data-testid="survey-camp">
            <SelectValue placeholder={t("parent.pickCampPlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/20 text-white">
            {camps.length === 0 ? (
              <SelectItem value="__none" disabled>{t("parent.noCampsToReview")}</SelectItem>
            ) : (
              camps.map((c) => {
                const done = surveyedIds.has(c.id);
                return (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2">
                      {c.name}
                      {!done && <span className="text-burns-gold text-[10px] uppercase">· pending</span>}
                      {done && <span className="text-green-400 text-[10px] uppercase">· done</span>}
                    </span>
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>

      {existingForCamp ? (
        <div className="bg-zinc-950 border border-burns-gold/30 rounded-md p-4 space-y-3">
          <p className="text-burns-gold text-xs flex items-center gap-1">
            <RotateCw className="w-3 h-3" />
            {t("parent.surveyLockedBanner").replace(
              "{date}",
              new Date(existingForCamp.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            )}
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-zinc-400">{t("parent.surveyQ1")}</div><div className="text-white">{existingForCamp.overall_rating}/5</div>
            <div className="text-zinc-400">{t("parent.surveyQ2")}</div><div className="text-white">{existingForCamp.coach_rating}/5</div>
            <div className="text-zinc-400">{t("parent.surveyQ3")}</div><div className="text-white">{existingForCamp.communication_rating}/5</div>
            <div className="text-zinc-400">{t("parent.surveyQ4")}</div><div className="text-white">{existingForCamp.would_recommend}/5</div>
          </div>
          {existingForCamp.favorite_part && (<div><p className="text-zinc-400 text-xs">{t("parent.surveyFavorite")}</p><p className="text-zinc-200 text-sm whitespace-pre-wrap">{existingForCamp.favorite_part}</p></div>)}
          {existingForCamp.most_improved_skill && (<div><p className="text-zinc-400 text-xs">{t("parent.surveyImproved")}</p><p className="text-zinc-200 text-sm whitespace-pre-wrap">{existingForCamp.most_improved_skill}</p></div>)}
          {existingForCamp.suggestions && (<div><p className="text-zinc-400 text-xs">{t("parent.surveySuggestions")}</p><p className="text-zinc-200 text-sm whitespace-pre-wrap">{existingForCamp.suggestions}</p></div>)}
          {existingForCamp.other_comments && (<div><p className="text-zinc-400 text-xs">{t("parent.surveyOther")}</p><p className="text-zinc-200 text-sm whitespace-pre-wrap">{existingForCamp.other_comments}</p></div>)}
        </div>
      ) : (
        <>
          <p className="text-zinc-500 text-xs">{t("parent.surveyScale")}</p>
          <div className="bg-zinc-950 border border-white/10 rounded-md p-4">
            <Question label={t("parent.surveyQ1")} value={overall} onChange={setOverall} />
            <Question label={t("parent.surveyQ2")} value={coach} onChange={setCoach} />
            <Question label={t("parent.surveyQ3")} value={comm} onChange={setComm} />
            <Question label={t("parent.surveyQ4")} value={recommend} onChange={setRecommend} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-sm">{t("parent.surveyFavorite")}</Label>
              <Textarea rows={2} value={favorite} onChange={(e) => setFavorite(e.target.value)} className="bg-zinc-950 border-white/20 text-white mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">{t("parent.surveyImproved")}</Label>
              <Textarea rows={2} value={improved} onChange={(e) => setImproved(e.target.value)} className="bg-zinc-950 border-white/20 text-white mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">{t("parent.surveySuggestions")}</Label>
              <Textarea rows={2} value={suggestions} onChange={(e) => setSuggestions(e.target.value)} className="bg-zinc-950 border-white/20 text-white mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">{t("parent.surveyOther")}</Label>
              <Textarea rows={2} value={other} onChange={(e) => setOther(e.target.value)} className="bg-zinc-950 border-white/20 text-white mt-1" />
            </div>
          </div>

          <Button
            type="submit"
            disabled={sending || !campId || camps.length === 0}
            className="bg-burns-red hover:bg-burns-red/80 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {t("parent.surveySubmit")}
          </Button>
        </>
      )}
    </form>
  );
}
